import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Package, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ALL_PERMISSIONS } from "@/lib/permissions";

// Modules that can be toggled (exclude always-on ones)
const TOGGLEABLE_MODULES = ALL_PERMISSIONS.filter(
  p => !["dashboard", "settings", "subscription", "support"].includes(p.key)
);

// Plan defaults for reference
const PLAN_DEFAULTS: Record<string, string[]> = {
  basic: ["staff", "children", "classes", "attendance", "parents", "billing", "salary_report", "observations"],
  standard: ["staff", "children", "classes", "attendance", "parents", "billing", "salary_report", "observations", "lessons", "assignments", "live_sessions", "interactive_learning", "reports", "analytics"],
  premium: ["staff", "children", "classes", "attendance", "parents", "billing", "salary_report", "observations", "lessons", "assignments", "live_sessions", "interactive_learning", "reports", "analytics", "game_reports"],
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nursery: { id: string; name: string; plan?: string } | null;
}

const NurseryModulesDialog = ({ open, onOpenChange, nursery }: Props) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [initialOverrides, setInitialOverrides] = useState<Record<string, boolean>>({});

  const planDefaults = PLAN_DEFAULTS[nursery?.plan || "basic"] || PLAN_DEFAULTS.basic;

  useEffect(() => {
    if (!open || !nursery) return;
    setLoading(true);
    
    supabase
      .from("nursery_modules" as any)
      .select("module_key, is_enabled")
      .eq("nursery_id", nursery.id)
      .then(({ data }) => {
        const map: Record<string, boolean> = {};
        (data || []).forEach((row: any) => {
          map[row.module_key] = row.is_enabled;
        });
        setOverrides(map);
        setInitialOverrides(map);
        setLoading(false);
      });
  }, [open, nursery]);

  const getEffectiveState = (key: string): boolean => {
    if (key in overrides) return overrides[key];
    return planDefaults.includes(key);
  };

  const isOverridden = (key: string): boolean => {
    return key in overrides;
  };

  const toggleModule = (key: string) => {
    const defaultState = planDefaults.includes(key);
    const currentState = getEffectiveState(key);
    const newState = !currentState;

    if (newState === defaultState && key in overrides) {
      // Remove override if it matches default
      const next = { ...overrides };
      delete next[key];
      setOverrides(next);
    } else {
      setOverrides(prev => ({ ...prev, [key]: newState }));
    }
  };

  const resetToDefaults = () => {
    setOverrides({});
  };

  const handleSave = async () => {
    if (!nursery || !user) return;
    setSaving(true);

    try {
      // Delete all existing overrides for this nursery
      await supabase
        .from("nursery_modules" as any)
        .delete()
        .eq("nursery_id", nursery.id);

      // Insert new overrides only
      const entries = Object.entries(overrides);
      if (entries.length > 0) {
        const rows = entries.map(([key, enabled]) => ({
          nursery_id: nursery.id,
          module_key: key,
          is_enabled: enabled,
          updated_by: user.id,
        }));

        const { error } = await supabase
          .from("nursery_modules" as any)
          .insert(rows as any);

        if (error) throw error;
      }

      setInitialOverrides({ ...overrides });
      toast.success("تم حفظ إعدادات الموديولات بنجاح");
      onOpenChange(false);
    } catch (e: any) {
      toast.error("فشل في حفظ الإعدادات");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(overrides) !== JSON.stringify(initialOverrides);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            إدارة موديولات: {nursery?.name}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            الخطة الحالية: <Badge variant="secondary">{nursery?.plan === "premium" ? "مميز" : nursery?.plan === "standard" ? "قياسي" : "أساسي"}</Badge>
            {" — "}تعديل الموديولات يتجاوز إعدادات الخطة
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <ScrollArea className="max-h-[400px]">
              <div className="space-y-1 pr-2">
                {TOGGLEABLE_MODULES.map(mod => {
                  const enabled = getEffectiveState(mod.key);
                  const overridden = isOverridden(mod.key);
                  const defaultState = planDefaults.includes(mod.key);

                  return (
                    <div
                      key={mod.key}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                        overridden ? "border-primary/30 bg-primary/5" : "border-transparent"
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <mod.icon className={`w-4 h-4 shrink-0 ${enabled ? "text-primary" : "text-muted-foreground"}`} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${enabled ? "text-foreground" : "text-muted-foreground"}`}>
                              {mod.label}
                            </span>
                            {overridden && (
                              <Badge variant="outline" className="text-[10px] px-1 h-4">
                                {enabled && !defaultState ? "مضاف" : !enabled && defaultState ? "معطّل" : "معدّل"}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">{mod.description}</p>
                        </div>
                      </div>
                      <Switch
                        checked={enabled}
                        onCheckedChange={() => toggleModule(mod.key)}
                      />
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            <div className="flex items-center gap-2 pt-2 border-t">
              <Button variant="ghost" size="sm" onClick={resetToDefaults} disabled={Object.keys(overrides).length === 0}>
                إعادة للافتراضي
              </Button>
              <div className="flex-1" />
              <Button variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
              <Button onClick={handleSave} disabled={!hasChanges || saving} className="gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                حفظ
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NurseryModulesDialog;
